/**
 * kartBirlestir.js — bir kişinin fuardaki firma başına kart kararlarını TEK karara indirir (backlog #400).
 *
 * Kullanan iki yer: Kart Yazdır penceresi (KartYazdirModal) ve Kişi Detay › Giriş Kartı sekmesi (GirisKartiSekmesi).
 * Girdi, `GET /personnel/{id}/kart-karari` (ya da `/detay`) `firmalar[]` listesidir: her girdi o firmanın kararını,
 * `uygunsuzluklar[]`ını, `kartBasilabilir`ini ve `kartErisim`ini taşır. Kural backend'de; burası yalnızca "ekranda ne
 * söylenecek"i seçer.
 *
 * Neden birleştirme: kişisel belgeler (SGK, İSG, Sağlık, KVKK, kimlik doğrulama) kişinin kendisinindir; firma başına
 * aynı "SGK eksik" satırını tekrar tekrar yazmak, birden çok firmalı kişide karışıklık yaratıyordu. Mesleki Eğitim ise
 * işe alıma (meslek) bağlı olduğu için zaten kendi satırında meslek adlarıyla gelir (KartUygunsuzluklari.eksikMeslekMetni).
 * Firmaya özgü olan tek şey FİRMA evrakıdır (Firma Belgeleri, firmanın İSG belgeleri, vergi numarası):
 *   · en az bir firmanın evrakı tamamsa kart onun üzerinden çıkar → firma evrakı HİÇ uyarılmaz,
 *   · hepsinin evrakı eksikse uyarılır ve bağlantı, hangi firmanın profiline gidileceğini sorar.
 *
 * Seçilen ("en iyi") firma kartın basılacağı firmadır: önce basılabilir olan, sonra tam erişimli (24 saatlik olmayan), sonra
 * kişisel engeli en az olan, sonra çağıranın tercihi (satırın en yeşil firması).
 */

/** Firmaya özgü evrak engelleri; `VERGI_NO` backend kodu değil, `kartErisim.taxNumberSet === false`ten türetilir. */
export const FIRMA_EVRAK_KODLARI = ["FIRMA_BELGELERI", "FIRMA_GENEL_BELGELERI", "VERGI_NO"];
const FIRMA_REDDETTI = "FIRMA_REDDETTI";

function siniflandir(girdi) {
  const kodlar = girdi?.uygunsuzluklar || [];
  const firmaEvrak = kodlar.filter((k) => FIRMA_EVRAK_KODLARI.includes(k));
  // WAS (until #435): if (girdi?.kartErisim?.taxNumberSet === false && !firmaEvrak.includes("VERGI_NO")) firmaEvrak.push("VERGI_NO");
  // #435: bir Yabancı firma vergi numarası borçlu değildir (taxNumberGerekli false, backend #434): eksik numara kartı engellemez.
  if (girdi?.kartErisim?.taxNumberSet === false && girdi?.kartErisim?.taxNumberGerekli !== false
    && !firmaEvrak.includes("VERGI_NO")) firmaEvrak.push("VERGI_NO");
  return {
    girdi,
    firmaEvrak,
    reddetti: kodlar.includes(FIRMA_REDDETTI),
    kisisel: kodlar.filter((k) => !FIRMA_EVRAK_KODLARI.includes(k) && k !== FIRMA_REDDETTI),
  };
}

const tamErisim = (x) => x.girdi?.kartErisim?.cardAccessMode !== "TEMPORARY_24H";

/**
 * @param {object[]} firmalar        kart-karari `firmalar[]`
 * @param {number}   tercihFirmaId   eşitlikte öne alınacak firma (satırın en yeşil firması); yoksa liste sırası
 * @param {number}   sabitFirmaId    verilirse YALNIZCA bu firma değerlendirilir (listede firma süzgeci açıkken)
 * @returns {null | {
 *   secili: object,              kartın basılacağı firmanın girdisi
 *   uygunsuzluklar: string[],    ekranda gösterilecek engel kodları (firma evrakı yalnızca hepsinde eksikse içinde)
 *   vergiNoYok: boolean,         "Firmanın vergi numarası yok" satırı eklensin mi
 *   firmaEvrakGoster: boolean,   hiçbir firmanın evrakı tam değil
 *   firmaSecenekleri: object[],  firma evrakı bağlantısının sorduğu firmalar ({ firmaId, firmaName, ... })
 *   digerFirmaSayisi: number,    değerlendirilen öteki firma sayısı
 *   digerFirmalar: object[],     o öteki firmaların girdileri (#479: künye başlığının "+N" ipucu adlarını sıralar)
 * }}
 */
export function kartKararBirlestir(firmalar, { tercihFirmaId = null, sabitFirmaId = null } = {}) {
  let liste = (firmalar || []).map(siniflandir);
  if (sabitFirmaId) {
    const sabit = liste.filter((x) => Number(x.girdi.firmaId) === Number(sabitFirmaId));
    if (sabit.length) liste = sabit;
  }
  if (liste.length === 0) return null;

  // Firmanın eşlemeyi reddettiği bir firma kart veremez; başka firma varsa o hiç hesaba girmez.
  const reddetmeyen = liste.filter((x) => !x.reddetti);
  const havuz = reddetmeyen.length ? reddetmeyen : liste;
  const evrakTam = havuz.filter((x) => x.firmaEvrak.length === 0);
  const firmaEvrakGoster = evrakTam.length === 0;
  const adaylar = firmaEvrakGoster ? havuz : evrakTam;

  const tercih = Number(tercihFirmaId) || null;
  const en = [...adaylar].sort((a, b) => (
    (Number(Boolean(b.girdi.kartBasilabilir)) - Number(Boolean(a.girdi.kartBasilabilir)))
    || (Number(tamErisim(b)) - Number(tamErisim(a)))
    || (a.kisisel.length - b.kisisel.length)
    || (Number(Number(b.girdi.firmaId) === tercih) - Number(Number(a.girdi.firmaId) === tercih))
  ))[0];

  return {
    secili: en.girdi,
    uygunsuzluklar: (en.girdi.uygunsuzluklar || []).filter((k) => firmaEvrakGoster || !FIRMA_EVRAK_KODLARI.includes(k)),
    vergiNoYok: firmaEvrakGoster && en.firmaEvrak.includes("VERGI_NO"),
    firmaEvrakGoster,
    firmaSecenekleri: firmaEvrakGoster ? havuz.map((x) => x.girdi) : [],
    digerFirmaSayisi: liste.length - 1,
    digerFirmalar: liste.filter((x) => x !== en).map((x) => x.girdi),
  };
}
